import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { AuthContext } from '../../Contexts/AuthContext';
import CONSTANTS from '../../Constants/ApiConstants';
import { useApi } from '../../Helpers/ApiHook';
import { Button } from '../../Components/Button';
import { Input } from '../../Components/Input';
import { Select } from '../../Components/Select';
import { useLocation, useNavigate } from 'react-router-dom';

// --- YUP VALIDATION SCHEMAS ---
const mobileEntrySchema = yup.object({
    country_code: yup.string().required('Code is required'),
    mobile_no: yup.string()
        .required('Mobile number is required')
        .matches(/^[0-9]{10,12}$/, 'Enter a valid 10-12 digit number'),
});

const otpSchema = yup.object({
    otp: yup.string()
        .required('OTP is required')
        .matches(/^[0-9]{4}$/, 'OTP must be 6 digits'),
});

const UserLoginModal = ({ }) => {
    const location = useLocation();
    const navigate = useNavigate()
    const { post } = useApi();
    const { isLoginModal, setIsLoginModal, signIn, setIsLoading } = useContext(AuthContext);

    // --- 1. STATE HOOKS (Must be unconditional) ---
    const [step, setStep] = useState(1); // 1: Mobile, 2: OTP
    const [submitMessage, setSubmitMessage] = useState('');
    const [submittedMobile, setSubmittedMobile] = useState('');
    const [messageId, setMessageId] = useState(null);

    // --- 2. MOBILE ENTRY FORMIK HOOK (Must be unconditional) ---
    const mobileFormik = useFormik({
        initialValues: { country_code: '+91', mobile_no: '' },
        validationSchema: mobileEntrySchema,
        onSubmit: async (values, { setSubmitting }) => {
            await setSubmitting(true);
            await setSubmitMessage('');

            // API Endpoint to request OTP (Placeholder)
            const API_ENDPOINT = CONSTANTS.API_URL + 'send-otp';

            try {
                // Simulate API call to send OTP
                const response = await axios({
                    url: API_ENDPOINT,
                    method: 'POST',
                    data: values
                });

                // Simulate a successful response indicating OTP sent
                if ([200, 201].includes(response.status)) {
                    const responseData = response.data || {};
                    // console.log("🚀 ~ UserLoginModal ~ responseData:", responseData)
                    await setSubmittedMobile(values.mobile_no || "");
                    await setMessageId(responseData?.response?.message);
                    await setSubmitMessage('OTP sent successfully! Redirecting...');

                    // Transition to step 2 after a brief message delay
                    setTimeout(() => {
                        setStep(2);
                        setSubmitting(false);
                        setSubmitMessage('');
                    }, 1500);
                }

            } catch (error) {
                console.error('Mobile Submission Error:', error);
                setSubmitMessage('Failed to send OTP. Please try again.');
                setSubmitting(false);
            } finally {
            }
        },
    });

    // --- 3. OTP VERIFICATION FORMIK HOOK (Must be unconditional) ---
    const otpFormik = useFormik({
        initialValues: { otp: '' },
        validationSchema: otpSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            await setSubmitting(true);
            await setSubmitMessage('');

            // API Endpoint to verify OTP (Placeholder)
            const API_ENDPOINT = CONSTANTS.API_URL + 'verify-otp';

            try {
                // Simulate API call to verify OTP
                const verificationData = {
                    mobile_no: submittedMobile,
                    reqId: messageId,
                    otp: values.otp,
                };

                const response = await axios({
                    url: API_ENDPOINT,
                    method: 'POST',
                    data: verificationData
                });

                if ([200, 201].includes(response.status)) {
                    await setSubmitMessage('Login successful! Welcome back.');
                    await setMessageId(null);
                    await setIsLoading(true);
                    await signIn({
                        token: `Bearer ${response.data?.token}`,
                        userId: response.data?.userId,
                        user: response.data?.user,
                    })

                    await setSubmitting(true);
                    // Close modal after success
                    setTimeout(() => {
                        resetForm();
                        setIsLoginModal();
                        setSubmittedMobile("");
                        setStep(1);
                        mobileFormik?.setSubmitting(false);
                    }, 2000);
                }

            } catch (error) {
                console.error('OTP Verification Error:', error);
                setSubmitMessage('OTP verification failed. Please check the code.');
                setSubmitting(false);
            } finally {
                // setSubmitting(false);
            }
        },
    });

    // --- 4. EFFECT HOOK (Must be unconditional) ---
    // Reset state when the modal closes or opens
    useEffect(() => {
        if (isLoginModal) {
            setStep(1);
            setSubmitMessage('');
            setSubmittedMobile('');
            mobileFormik.resetForm();
            otpFormik.resetForm();
        }
    }, [isLoginModal]);

    // --- CONDITIONAL RENDER (Must be after all hooks) ---
    if (!isLoginModal) return null;

    // Helper function for dynamic input classes
    const getInputClasses = (formikInstance, fieldName) => {
        // const baseClasses = "w-full px-4 py-2 border rounded-lg text-gray-700 leading-tight focus:outline-none focus:ring-2 transition duration-150";
        const isInvalid = formikInstance.touched[fieldName] && formikInstance.errors[fieldName];

        if (isInvalid) {
            return `border-red-500 focus:border-red-500 focus:ring-red-200`;
        }
        return ``;
    };

    const getFormikError = (formikInstance, fieldName) => {
        return formikInstance.touched[fieldName] && formikInstance.errors[fieldName] ? formikInstance.errors[fieldName] : null;
    };

    const closeModal = () => {
        if ((location?.pathname || "").includes('/create')) {
            navigate(-1);
        }
        setIsLoginModal(false);
    }

    // console.log(mobileFormik.isSubmitting, otpFormik.isSubmitting)
    // --- RENDER UI (Tailwind Modal Structure with Sliding Content) ---
    return (
        // 1. Modal Overlay
        <div className="fixed inset-0 bg-gradient-radial z-50 flex items-center justify-center p-4" onClick={closeModal}>

            {/* 2. Modal Content Wrapper (Prevents closing on click and sets max width) */}
            <div
                className="w-full max-w-sm bg-black bg-gradient-radial shadow-2xl rounded-xl relative overflow-hidden transform transition-all"
                onClick={e => e.stopPropagation()}
                style={{ minHeight: '360px', maxHeight: '500px' }} // Fixed height for visual slide effect
            >
                {/* Close Button */}
                <button
                    type="button"
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition z-20"
                    onClick={closeModal}
                    aria-label="Close"
                >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>

                {/* Status Messages */}
                {submitMessage && (
                    <div className={`p-3 mx-6 mt-4 rounded-lg text-sm text-center font-medium ${submitMessage.includes('successful')
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                        } z-10 relative`}>
                        {submitMessage}
                    </div>
                )}

                {/* SLIDING CONTENT TRACK */}
                <div
                    className="flex w-[200%] h-full transition-transform duration-500 ease-in-out absolute inset-0"
                    style={{ transform: `translateX(-${(step - 1) * 50}%)` }} // Slide effect: 0% for Step 1, -50% for Step 2
                >

                    {/* --- STEP 1: MOBILE NUMBER ENTRY --- */}
                    <div className="w-1/2 flex-shrink-0 p-6 pt-0">
                        <div className="mt-8">
                            <h2 className="text-2xl font-bold text-white mb-6 text-center">
                                Sign In/Sign Up to <br /> your FWPI  Account
                            </h2>
                            <form onSubmit={mobileFormik.handleSubmit}>
                                {/* Mobile Number Input Group */}
                                <div className="flex mb-2">
                                    {/* Country Code Dropdown */}
                                    <div className="relative mr-2 w-1/3">
                                        <Select
                                            placeholder="Enter Country Code"
                                            name="country_code"
                                            isHideDefault={true}
                                            options={[{
                                                label: "+91",
                                                value: "+91"
                                            }]}
                                            {...mobileFormik.getFieldProps('country_code')}
                                        />
                                    </div>

                                    {/* Mobile Number Input */}
                                    <Input
                                        type="tel"
                                        placeholder="Enter mobile number"
                                        name="mobile_no"
                                        maxLength='10'
                                        {...mobileFormik.getFieldProps('mobile_no')}
                                    />
                                </div>

                                {getFormikError(mobileFormik, 'mobile_no') && (
                                    <p className="text-red-500 text-xs italic mt-1">{getFormikError(mobileFormik, 'mobile_no')}</p>
                                )}
                                <p className="text-sm text-gray-500 mb-8 mt-1">OTP will be sent to this number</p>

                                {/* Submit Button */}
                                <Button
                                    type='submit'
                                    width='w-full'
                                    disabled={mobileFormik.isSubmitting}
                                    loading={mobileFormik.isSubmitting}
                                >
                                    Next
                                </Button>
                                {/* <button
                                    type="submit"
                                    disabled={mobileFormik.isSubmitting}
                                    className={`w-full py-3 rounded-lg font-bold text-white transition duration-200 ${mobileFormik.isSubmitting
                                        ? 'bg-gray-400 cursor-not-allowed'
                                        : 'bg-black hover:bg-white hover:text-black focus:outline-none focus:ring-4 focus:ring-gray-300'
                                        }`}
                                >
                                    {mobileFormik.isSubmitting ? 'Sending OTP...' : 'Next'}
                                </button> */}

                                <p className="text-xs text-gray-500 text-center mt-6">
                                    By signing up, you agree to the <a href="/terms-and-conditions" className="text-blue-600 hover:underline font-medium">Terms of Service</a> and <a href="/privacy-policy" className="text-blue-600 hover:underline font-medium">Privacy Policy</a>.
                                </p>
                            </form>
                        </div>
                    </div>


                    {/* --- STEP 2: OTP VERIFICATION --- */}
                    <div className="w-1/2 flex-shrink-0 p-6 pt-0">
                        <div className="mt-8">
                            <button onClick={() => setStep(1)} className="text-blue-600 hover:text-blue-800 transition mb-4 flex items-center">
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
                                Change Number
                            </button>

                            <h2 className="text-2xl font-bold text-white mb-2 text-center">
                                Enter code sent to <br /> your number
                            </h2>
                            <p className="text-sm text-gray-700 mb-6 text-center">
                                Sent to <span className="font-semibold text-gray-400">+91 {submittedMobile}</span>
                            </p>

                            <form onSubmit={otpFormik.handleSubmit}>

                                {/* OTP Input */}
                                <div className="mb-4">
                                    <Input
                                        placeholder="----"
                                        maxLength="4"
                                        name="otp"
                                        {...otpFormik.getFieldProps('otp')}
                                        className="text-center font-inter font-semibold text-xl tracking-[1rem] pl-[1.5rem]"
                                        onKeyUp={(e) => {
                                            const value = e.target.value || "";
                                            // console.log(value)
                                            if (value.length > 3) {
                                                document.getElementById('button_otp').focus();
                                            }
                                        }}
                                    // onFocus={() => {
                                    //     const value = otpFormik?.values?.otp || "";
                                    //     if ((value).length > 3) {
                                    //         otpFormik?.setFieldValue('otp', value.substring(0, value.length - 1))
                                    //     }
                                    // }}
                                    />
                                    {getFormikError(otpFormik, 'otp') && (
                                        <p className="text-red-500 text-xs italic mt-1 text-center">{getFormikError(otpFormik, 'otp')}</p>
                                    )}
                                </div>

                                <p className="text-center text-sm text-blue-600 mb-8 mt-2">
                                    <a href="#" onClick={(e) => { e.preventDefault(); /* Resend logic here */ }} className="hover:underline font-semibold">
                                        Didn't receive SMS? Send Again
                                    </a>
                                </p>

                                {/* Submit Button */}
                                <Button
                                    id="button_otp"
                                    type='submit'
                                    width='w-full'
                                    disabled={otpFormik.isSubmitting}
                                    loading={otpFormik.isSubmitting}
                                >
                                    Verify Otp
                                </Button>

                                <p className="text-xs text-gray-500 text-center mt-6">
                                    no credit card/payment details needed
                                </p>
                            </form>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export { UserLoginModal };